<template>
  <div class="comments-section">
    <!-- 发布评论表单 -->
    <div class="card p-6 mb-8">
      <div class="flex items-center gap-2 mb-6">
        <div class="icon-box-blue flex-shrink-0">
          <span>💬</span>
        </div>
        <h3 class="text-xl font-heading font-bold text-clay">发表评论</h3>
      </div>

      <form @submit.prevent="publishComment" class="space-y-4">
        <div>
          <label for="comment-content" class="block text-sm font-medium text-gray-700 mb-2">评论内容 *</label>
          <textarea
            id="comment-content"
            v-model="newComment"
            rows="4"
            placeholder="写下你的想法..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :disabled="publishing"
          ></textarea>
        </div>

        <p v-if="!userLoggedIn" class="text-xs text-gray-500">未登录将以游客身份发表，提交前会先审核。</p>

        <button 
          type="submit" 
          class="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="publishing || !newComment.trim()">
          {{ publishing ? '发布中...' : '发布评论' }}
        </button>
      </form>
    </div>

    <!-- 评论列表 -->
    <div class="card p-6">
      <div class="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-100">
        <h4 class="font-heading font-semibold text-lg text-clay">
          {{ total }} 条评论
        </h4>
        
        <!-- 排序选择 -->
        <select 
          v-model="sortType" 
          @change="loadComments(1)"
          class="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="recent">最新</option>
          <option value="hot">最热</option>
        </select>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-8 text-gray-500">
        <span>加载中...</span>
      </div>

      <!-- 评论列表 -->
      <ul v-else-if="comments.length > 0" class="space-y-4">
        <li 
          v-for="comment in comments" 
          :key="comment.id"
          class="flex gap-4 p-4 rounded-lg hover:shadow-md transition-shadow"
          :class="comment.kind === 'answer' ? 'border border-dashed border-primary/40 bg-secondary/60' : 'bg-cream border-2 border-gray-100'">
          <!-- 头像 -->
          <img
            v-if="comment.user?.avatar"
            :src="comment.user.avatar"
            :alt="comment.user.displayName || comment.user.username"
            class="w-10 h-10 rounded-full flex-shrink-0 border-2 border-white shadow-sm"
          />
          <div 
            v-else 
            class="w-10 h-10 rounded-full flex-shrink-0 border-2 border-white shadow-sm bg-gray-100 flex items-center justify-center text-xl"
            title="未知用户"
          >
            👤
          </div>
          
          <!-- 内容 -->
          <div class="flex-1">
            <div class="flex justify-between items-center mb-1">
              <div class="font-heading font-semibold text-clay">
                <span v-if="comment.kind === 'answer'" class="mr-2 rounded-sm bg-highlight px-2 text-xs font-medium text-clay">谜底</span>
                {{ commenterName(comment) }}
              </div>
              <div class="flex items-center gap-2">
                <span v-if="comment.submitterIp" class="text-xs text-gray-500">{{ comment.submitterIp }}</span>
                <span class="text-xs text-gray-500">
                  {{ formatTime(comment.createdAt) }}
                </span>
                <button
                  v-if="canDelete(comment)"
                  @click="deleteComment(comment.id)"
                  class="text-xs text-red-500 hover:text-red-700 hover:underline">
                  删除
                </button>
              </div>
            </div>
            
            <p class="text-gray-700 font-body text-sm leading-relaxed mb-2 whitespace-pre-wrap">
              {{ comment.kind === 'answer' ? answerSentence(comment) : comment.content }}
            </p>
            
            <!-- 回复按钮 -->
            <button
              @click="replyTo(comment)"
              class="text-xs text-blue-500 hover:text-blue-700 hover:underline">
              回复
            </button>
          </div>
        </li>
      </ul>

      <!-- 空状态 -->
      <div v-else class="text-center py-8 text-gray-500">
        <p>暂无评论，快来发表第一条评论吧！</p>
      </div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="mt-6 flex justify-center gap-2">
        <button
          @click="loadComments(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          上一页
        </button>
        <span class="px-4 py-2 text-sm text-gray-700">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <button
          @click="loadComments(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          下一页
        </button>
      </div>
    </div>

    <!-- 回复对话框 -->
    <div 
      v-if="replyingTo" 
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="cancelReply">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-lg font-bold mb-4">回复评论</h3>
        <p class="text-sm text-gray-600 mb-4">
          回复 <span class="font-semibold">{{ commenterName(replyingTo) }}</span>:
        </p>
        <textarea
          v-model="replyContent"
          rows="4"
          placeholder="写下你的回复..."
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          autofocus
        ></textarea>
        <div class="flex gap-2 justify-end">
          <button @click="cancelReply" class="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">取消</button>
          <button 
            @click="submitReply" 
            class="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
            :disabled="!replyContent.trim() || replying">
            {{ replying ? '发送中...' : '发送回复' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

interface Comment {
  id: string | number
  content: string
  kind?: string
  submitterIp?: string
  createdAt: number
  user?: {
    id: string
    username: string
    displayName: string
    avatar: string
    rank: number
  }
}

const props = defineProps<{
  riddleId: string
  tenantId: string
  userLoggedIn: boolean
  userId: string
  userName: string
  userAvatar: string
  commentCount?: string
}>()

// 评论列表
const comments = ref<Comment[]>([])
const loading = ref(false)
const total = ref(parseInt(props.commentCount || '0'))
const currentPage = ref(1)
const pageSize = 20
const sortType = ref('recent')

// 发布评论
const newComment = ref('')
const publishing = ref(false)

// 回复评论
const replyingTo = ref<Comment | null>(null)
const replyContent = ref('')
const replying = ref(false)

// 计算属性
const totalPages = computed(() => Math.ceil(total.value / pageSize))

// 加载评论列表
async function loadComments(page = 1) {
  if (page < 1) return
  
  loading.value = true
  currentPage.value = page
  
  try {
    const params = new URLSearchParams({
      riddle_id: props.riddleId,
      page: page.toString(),
      size: pageSize.toString(),
      sort: sortType.value,
    })
    
    const headers: Record<string, string> = {}
    if (props.tenantId && props.tenantId !== '0') {
      headers['X-Tenant-ID'] = props.tenantId
    }
    
    const response = await fetch(`/v1/riddles/comments?${params}`, { headers })
    const data = await response.json()
    
    if (data.code === 0 && data.data) {
      if (data.data.list) {
        comments.value = data.data.list || []
        total.value = data.data.pagination?.total || 0
      } else {
        // Fallback for old format or empty
        comments.value = data.data.items || []
        total.value = data.data.total || 0
      }
    } else {
      console.error('加载评论失败:', data.message)
      showToast(data.message || '加载评论失败', 'error')
    }
  } catch (error) {
    console.error('加载评论失败:', error)
    showToast('网络错误，请稍后重试', 'error')
  } finally {
    loading.value = false
  }
}

// 发布评论
async function publishComment() {
  if (!newComment.value.trim()) return
  
  publishing.value = true
  
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (props.tenantId && props.tenantId !== '0') {
      headers['X-Tenant-ID'] = props.tenantId
    }
    
    const response = await fetch('/v1/riddles/comment/publish', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        riddle_id: parseInt(props.riddleId),
        content: newComment.value.trim(),
      }),
    })
    
    const data = await response.json()
    
    if (data.code === 0) {
      newComment.value = ''
      showToast('评论已公开', 'success')
      
      // 重新加载第一页并更新总数
      await loadComments(1)
    } else {
      showToast(data.message || '评论发表失败', 'error')
    }
  } catch (error) {
    console.error('发布评论失败:', error)
    showToast('网络错误，请稍后重试', 'error')
  } finally {
    publishing.value = false
  }
}

// 回复评论
function replyTo(comment: Comment) {
  replyingTo.value = comment
  replyContent.value = ''
}

function cancelReply() {
  replyingTo.value = null
  replyContent.value = ''
}

async function submitReply() {
  if (!replyContent.value.trim() || !replyingTo.value) return
  
  replying.value = true
  
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (props.tenantId && props.tenantId !== '0') {
      headers['X-Tenant-ID'] = props.tenantId
    }
    
    const response = await fetch('/v1/riddles/comment/reply', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        parentId: replyingTo.value.id,
        content: replyContent.value.trim(),
      }),
    })
    
    const data = await response.json()
    
    if (data.code === 0) {
      showToast('回复已公开', 'success')
      cancelReply()
      await loadComments(currentPage.value)
    } else {
      showToast(data.message || '回复失败', 'error')
    }
  } catch (error) {
    console.error('回复失败:', error)
    showToast('网络错误，请稍后重试', 'error')
  } finally {
    replying.value = false
  }
}

// 删除评论
async function deleteComment(commentId: string | number) {
  if (!confirm('确定要删除这条评论吗？')) return
  
  try {
    const headers: Record<string, string> = {}
    if (props.tenantId && props.tenantId !== '0') {
      headers['X-Tenant-ID'] = props.tenantId
    }
    
    const response = await fetch(`/v1/riddles/comment/${commentId}`, {
      method: 'DELETE',
      headers,
    })
    
    const data = await response.json()
    
    if (data.code === 0) {
      showToast('评论已删除', 'success')
      await loadComments(currentPage.value)
    } else {
      showToast(data.message || '删除失败', 'error')
    }
  } catch (error) {
    console.error('删除评论失败:', error)
    showToast('网络错误，请稍后重试', 'error')
  }
}

// 判断是否可以删除评论
function canDelete(comment: Comment): boolean {
  return props.userLoggedIn && comment.user?.id === props.userId
}

function commenterName(comment: Comment): string {
  return comment.user?.displayName || comment.user?.username || '游客'
}

function answerSentence(comment: Comment): string {
  return `${commenterName(comment)}认为的谜底是「${comment.content}」`
}

function onAnswerComment(event: Event) {
  const detail = (event as CustomEvent<{ riddleId?: string; comment?: Comment }>).detail
  if (!detail || String(detail.riddleId) !== String(props.riddleId) || !detail.comment) {
    return
  }
  const incoming = detail.comment
  if (currentPage.value !== 1 || sortType.value !== 'recent') {
    sortType.value = 'recent'
    loadComments(1)
    return
  }
  const existed = comments.value.some((item) => item.id === incoming.id)
  comments.value = [incoming, ...comments.value.filter((item) => item.id !== incoming.id)]
  if (!existed) {
    total.value += 1
  }
}

// 时间格式化
function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diff < 60) return '刚刚'
  if (diff < 3600) return Math.floor(diff / 60) + '分钟前'
  if (diff < 86400) return Math.floor(diff / 3600) + '小时前'
  if (diff < 2592000) return Math.floor(diff / 86400) + '天前'
  
  return date.toLocaleDateString('zh-CN')
}

// Toast 提示（使用全局函数）
function showToast(message: string, type: 'success' | 'error' | 'info') {
  if (typeof (window as any).showToast === 'function') {
    (window as any).showToast(message, type)
  } else {
    alert(message)
  }
}

// 组件挂载时加载评论，并接收本题猜底成功后插入的谜底评论
onMounted(() => {
  document.addEventListener('riddle-answer-comment', onAnswerComment)
  loadComments(1)
})

onUnmounted(() => {
  document.removeEventListener('riddle-answer-comment', onAnswerComment)
})
</script>
